/* eslint-disable max-len */
import React from 'react';
import { ReferenceBrandType } from '@1po/1po-bff-fe-spec/generated/catalog/references/common/model/ReferenceBrandType';
import { VehicleBrandType } from '@1po/1po-bff-fe-spec/generated/common/vehicle/VehicleDetail';
import styled, { useTheme } from 'styled-components';
import { ReactComponent as AlpineLogo } from 'assets/CarBrand/brand_alpine.svg';
import { ReactComponent as ComingSoonLogo } from 'assets/CarBrand/brand_coming_soon.svg';
import { ReactComponent as DaciaLogo } from 'assets/CarBrand/brand_dacia.svg';
import { ReactComponent as MotrioLogo } from 'assets/CarBrand/brand_motrio.svg';
import renativePng from 'assets/CarBrand/brand_renative.png';
import { ReactComponent as RenativeLogo } from 'assets/CarBrand/brand_renative.svg';
import renativeRemanPng from 'assets/CarBrand/brand_renative_reman.png';
import { ReactComponent as RenativeRemanLogo } from 'assets/CarBrand/brand_renative_reman.svg';
import { ReactComponent as RenaultLogo } from 'assets/CarBrand/brand_renault.svg';
import { ReactComponent as ValuePlusLogo } from 'assets/CarBrand/brand_value_plus.svg';
import { ReactComponent as CastrolLogo } from 'assets/logos/brand_castrol.svg';
import { ReactComponent as IxellLogo } from 'assets/logos/brand_ixell.svg';
import { ReactComponent as IxellCareLogo } from 'assets/logos/brand_ixellCare.svg';
import { checkRenative } from 'pages/UniversalProductsPage/ProductsPage/Product.utils';
import { Icon, IconType } from 'UI';

const RenativePngImg = styled.img<{ $noPointer?: boolean }>`
  display: inline-block;
  border-radius: 2px;
  object-fit: cover;
  cursor: ${({ $noPointer }) => ($noPointer ? 'auto' : 'pointer')};
`;

const pngBySvgLogo = new Map<IconType, { src: string; alt: string }>([
  [RenativeLogo, { src: renativePng, alt: 'RENATIVE' }],
  [RenativeRemanLogo, { src: renativeRemanPng, alt: 'RENATIVE reman' }],
]);

const getVehicleBrandLogo = (hasRenativeLogo: boolean, vehicleBrand?: VehicleBrandType) => {
  if (hasRenativeLogo) {
    return RenativeLogo;
  }

  switch (vehicleBrand) {
    case 'DACIA':
      return DaciaLogo;
    case 'RENAULT':
      return RenaultLogo;
    case 'ALPINE':
      return AlpineLogo;
    default:
      return false;
  }
};

const getIcon = (hasRenativeLogo: boolean, referenceBrand?: ReferenceBrandType, vehicleBrand?: VehicleBrandType) => {
  if (hasRenativeLogo) {
    return RenativeLogo;
  }

  switch (referenceBrand) {
    case 'IAM':
    case 'CENTRAL': {
      return getVehicleBrandLogo(hasRenativeLogo, vehicleBrand);
    }
    case 'STANDARD_EXCHANGE': {
      return RenativeRemanLogo;
    }
    case 'VALUE_PLUS': {
      return ValuePlusLogo;
    }
    case 'MOTRIO': {
      return MotrioLogo;
    }
    case 'IXELL': {
      return IxellLogo;
    }
    case 'IXTAR': {
      return IxellCareLogo;
    }
    case 'CASTROL': {
      return CastrolLogo;
    }
    default: {
      return getVehicleBrandLogo(hasRenativeLogo, vehicleBrand);
    }
  }
};

export const BrandImage = ({
  referenceBrand,
  vehicleBrand,
  familyCode,
  size = 121,
  width,
  height,
  noPointer,
  renative,
  fallbackComponent,
  usePng,
}: {
  referenceBrand?: ReferenceBrandType;
  vehicleBrand?: VehicleBrandType;
  familyCode?: string;
  size?: number;
  width?: number;
  height?: number;
  noPointer?: boolean;
  renative?: boolean;
  fallbackComponent?: React.ReactNode;
  usePng?: boolean;
}) => {
  const { color } = useTheme();
  const hasRenativeLogo = checkRenative(familyCode, renative);
  const logo = getIcon(hasRenativeLogo, referenceBrand, vehicleBrand);

  const png = usePng && logo ? pngBySvgLogo.get(logo) : undefined;
  if (png) {
    return (
      <RenativePngImg
        src={png.src}
        alt={png.alt}
        width={width ?? size}
        height={height ?? size}
        $noPointer={noPointer}
        data-cy={'reference-image'}
      />
    );
  }

  if (!logo && fallbackComponent) {
    return fallbackComponent;
  } else {
    return (
      <Icon
        IconComponent={logo || ComingSoonLogo}
        background={color.shadow_7}
        size={size}
        width={width}
        height={height}
        noPointer={noPointer}
        data-cy={'reference-image'}
      />
    );
  }
};
